<!-- --------------------------------------------------------------------------- -->
<!-- Main screen displaying a splash screen until required resources are loaded, -->
<!-- then the login form (skipped when logged in) and the application layout     -->
<!-- --------------------------------------------------------------------------- -->
<template>
  <v-app :class="`bg--${$vuetify.theme.dark ? 'dark' : 'light'}`" class="scroll-container">
    <!-- Idle dialog -->
    <v-dialog v-model="isIdle" persistent max-width="290" style="z-index: 100000">
      <v-card>
        <v-card-title class="headline">Idle</v-card-title>
        <v-card-text>Bitte neu einloggen</v-card-text>
      </v-card>
    </v-dialog>

    <!-- Automatic update mode -->
    <v-dialog v-model="appUpdateExists" persistent max-width="290" style="z-index: 100000">
      <v-card>
        <v-card-title class="headline">{{ $t('app.update.title') }}</v-card-title>
        <v-card-text class="d-flex justify-center">{{ $t('app.updateAvailable', { timer: timer / 1000 }) }}</v-card-text>
      </v-card>
    </v-dialog>

    <v-bottom-sheet v-model="showInstallDialog" persistent>
      <v-sheet class="text-center" persistent>
        <v-card class="d-flex flex-column mx-auto justify-center">
          <v-card-title class="d-flex justify-center">{{ $t('app.install.title') }}</v-card-title>
          <v-card-text v-html="$t('install.prompt')"></v-card-text>
          <v-card-actions class="d-flex justify-center">
            <v-btn outlined @click="dismissInstall">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn outlined @click="dismissInstallAndDoNotAskAgain">{{ $t('install.doNotAskgain') }}</v-btn>
            <v-btn color="green" dark @click="install">
              {{ $t('install.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-sheet>
    </v-bottom-sheet>

    <acknowledge-dialog v-if="isReadyAndDataLoaded" />

    <!-- Login form -->
    <login v-if="isInLoginPhase && !useCloudAuth" />

    <!-- Welcome screen -->
    <welcome v-if="showWelcomeScreen"></welcome>

    <div v-if="!isReady" class="fullscreen-center-content gray--text white">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <!-- The very application -->
    <app-layout v-if="isReady" />
  </v-app>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import login from '@/screens/login/login.vue';
import Welcome from '@/screens/welcome/Welcome';
import AcknowledgeDialog from '@/dialogs/AcknowledgeDialog';
import { mapGetters, mapState } from 'vuex';
import interceptors from './interceptors';
import configMixin from '@/mixins/configMixin';
import { HOURS_IN_MS, MINUTES_IN_MS } from '@/common/constants';
import config from '@/common/config';

let installPrompt = null;

export default {
  mixins: [configMixin],
  components: {
    login,
    AppLayout,
    Welcome,
    AcknowledgeDialog,
  },
  created() {
    let themeName = localStorage.getItem('theme');
    this.$vuetify.theme.dark = themeName === 'dark';

    // EventListener to be notified when a service-worker has been updated. Registered in registerServiceWorker
    document.addEventListener('swUpdated', this.showRefreshUI, { once: true });

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', this.onServiceWorkerControllerChange);
    }
    // see https://medium.com/js-dojo/promoting-your-vue-js-pwa-installation-6bd112342c19
    // This event is triggered once after loading the app as soon it is ready to be installed
    window.addEventListener('beforeinstallprompt', (e) => this.onBeforeInstallPromptEvent(e));
    window.addEventListener('appinstalled', this.onAppInstalled);
    window.addEventListener('appinstalled', this.disableInAppInstallPrompt);
    window.addEventListener('click', (event) => interceptors.clickInterceptor(event, this.$router, this.$store.state));

    // most likely not necessary: this.checkCameraPermission();
  },
  data() {
    return {
      isIdle: false,
      //splashScreenDisplayIntervalCount: 0,
      refreshing: false,
      registration: null,
      updateExists: false,
      appUpdateExists: false,
      timer: 1000,
      isRefreshingOnRealAppUpdate: false,
      showInstallDialog: false,
      welcomeScreen: true,
    };
  },
  computed: {
    ...mapGetters('lifecycle', ['isOnStartup', 'isBootstrapping', 'isInLoginPhase', 'isReady', 'isAuthenticated']),
    ...mapGetters('welcome', ['showWelcomeScreen']),
    ...mapState('lifecycle', ['phase']),
    ...mapState('welcome', ['welcomeItems', 'welcomeItemToDisplay', 'showWelcome']),
    ...mapState('sync', ['dataSynchronizationComplete']),
    ...mapState(['configs']),
    ...mapState(['isPwaMode']),

    appVersion() {
      return config.appVersion;
    },

    cloudAuthProviderConfig() {
      return this.$store.state.configs.features.cloudAuthProvider;
    },

    isReadyAndDataLoaded() {
      return this.isReady && this.dataSynchronizationComplete;
    },
    useCloudAuth() {
      if (this.cloudAuthProviderConfig) {
        if (this.cloudAuthProviderConfig[this.stage]) {
          return !this.cloudAuthProviderConfig.manualLoginEnabled;
        }
      }
      return false;
    },
    moduleConfigs() {
      return this.$store.state.configs.moduleConfigs;
    },
  },

  methods: {
    async checkCameraPermission() {
      console.log('checkCameraPermission');
      const permissionAsked = localStorage.getItem('cameraPermissionAsked');
      if (permissionAsked && permissionAsked === 'true') {
        return true;
      }
      const hasPermission = await navigator.mediaDevices.getUserMedia({ video: true, camera: true });
      if (hasPermission) {
        localStorage.setItem('cameraPermissionAsked', 'true');
      }
      return hasPermission;
    },
    onServiceWorkerControllerChange() {
      console.log('app.vue: SW controllerchange received. Reason:', this.isRefreshingOnRealAppUpdate ? 'real app update' : 'Browser clear site data action.');

      if (this.refreshing) return; // skip, if a refreshing process is currently running

      this.refreshing = true;

      // To actually reload we need to refresh the page but not immediately as the browser is in a loop then.
      if (this.isRefreshingOnRealAppUpdate) {
        this.isRefreshingOnRealAppUpdate = false;
        this.$nextTick(() => {
          window.location.reload();
        });
      }
    },
    onBeforeInstallPromptEvent(e) {
      console.log('beforeinstallprompt received. Prevent the default install dialog to appear and defer custom installation prompt');
      // Prevents the default mini-infobar or install dialog from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      installPrompt = e;
      if (this.isAuthenticated) {
        console.log('ShowInstallPrompt because beforeinstallprompt received and the user is authenticated');
        this.showInstallPrompt();
      } else {
        console.log('Do not show install prompt right now because the user is not authenticated (the beforeinstallprompt event was stashed)');
      }
    },

    showInstallPrompt() {
      const pwaInstalledDismissDoNotAskAgain = localStorage.getItem('pwaInstalledDismissDoNotAskAgain');
      const pwaInstalledDismissDate = new Date(localStorage.getItem('pwaInstalledDismissDate'));

      let isPwaInstalledDismissDateBeforeWaitingPeriod = true; // in case no installation was dismissed, this default leads to displaying the install prompt
      const WAITING_TIME = 3 * 24 * HOURS_IN_MS; // ask every 3 days

      // if the installation has been dismissed previously the installation prompt will be displayed if the dismiss date is WAITING_TIME before now
      if (pwaInstalledDismissDate && pwaInstalledDismissDate.getMonth) {
        console.log('pwaInstalledDismissDateBeforeWaitingPeriod = Date.now() - pwaInstalledDismissDate.getTime() > WAITING_TIME;');
        console.log(`pwaInstalledDismissDateBeforeWaitingPeriod = ${Date.now()} - ${pwaInstalledDismissDate.getTime()} > ${WAITING_TIME};`);
        isPwaInstalledDismissDateBeforeWaitingPeriod = Date.now() - pwaInstalledDismissDate.getTime() > WAITING_TIME;
      }
      console.log(
        `showInstallPrompt if this.isAuthenticated=${
          this.isAuthenticated
        }, installPrompt=${!!installPrompt} pwaInstalledDismissDateBeforeWaitingPeriod=${isPwaInstalledDismissDateBeforeWaitingPeriod} pwaInstalledDismissDoNotAskAgain=${pwaInstalledDismissDoNotAskAgain}`
      );
      if (this.isAuthenticated && installPrompt && isPwaInstalledDismissDateBeforeWaitingPeriod && pwaInstalledDismissDoNotAskAgain !== 'true') {
        console.log('display install prompt as the application is in authenticated state');

        this.showInstallDialog = true;
      }
    },
    isInstalled() {
      return window.matchMedia('(display-mode: standalone)').matches;
    },
    dismissInstallAndDoNotAskAgain() {
      localStorage.setItem('pwaInstalledDismissDoNotAskAgain', 'true');
      this.dismissInstall();
    },
    dismissInstall() {
      console.log('dismissInstall');
      installPrompt = null;
      this.showInstallDialog = false;
      localStorage.setItem('pwaInstalledDismissDate', new Date().toISOString());
    },
    async install() {
      this.showInstallDialog = false;
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      console.log(`install outcome=${outcome}`);
      localStorage.setItem('pwaInstalledDAcceptDate', new Date().toISOString());
    },
    onAppInstalled() {
      console.log('app has been installed. Reset deferredPrompt');
      installPrompt = null;
    },
    showRefreshUI(e) {
      console.log("App received 'swUpdated' event. Update app when the app is idle for 2h or is on the homescreen");
      this.registration = e.detail;
      const canUpdate = this.canPerformUpdate();
      console.log(`canUpdate=${canUpdate}`);
      if (canUpdate) {
        console.log(`canUpdate=${canUpdate}`);

        this.showRefreshTimer();
      } else {
        console.log(`Update found but not on homescreen, check every minute if we can update. Route.path=${this.$route.path}`);
        console.log(`canUpdate=${canUpdate}`);
        const timeout = setTimeout(() => {
          const canUpdate = this.canPerformUpdate();
          console.log(`canUpdate=${canUpdate}`);
          this.showRefreshTimer();
        }, 1000 * 30);
      }
    },
    canPerformUpdate() {
      const idleStartStr = localStorage.getItem('idleStart');
      const idleStart = idleStartStr ? new Date(idleStartStr) : null;
      const IDLE_TIME_IN_MINUTES_BEFORE_UPDATE_ON_NON_HOME_PATH = 10;

      console.log(`canPerformUpdate on this.$route.path=${this.$route.path} `);
      if (idleStart && idleStart.getTime) {
        console.log(`idleStart=${idleStart} and Date.now()-idleStart.getTime()>1 * MINUTES_IN_MS`);
        console.log(`idleStart=${idleStart} and ${Date.now() - idleStart.getTime()}>${IDLE_TIME_IN_MINUTES_BEFORE_UPDATE_ON_NON_HOME_PATH * MINUTES_IN_MS}`);
      }
      const path = this.$route.path;
      // do not allow in forms, wagenbuch and begleitfahrt modules unless the view is idle for some time
      const isModuleWithDisabledAutoUpdate = path.includes('forms', 'unfalldesk', 'wagenbuch', 'begleitfahrt');
      const isIdleForDefinedTime =
        idleStart && idleStart.getTime && Date.now() - idleStart.getTime() > IDLE_TIME_IN_MINUTES_BEFORE_UPDATE_ON_NON_HOME_PATH * MINUTES_IN_MS;

      return path.length < 2 || !isModuleWithDisabledAutoUpdate || isIdleForDefinedTime;
    },

    showRefreshTimer() {
      if (this.features.silentAppUpdates) {
        console.log('perform a silent update');
        this.refreshApp();
      } else {
        this.appUpdateExists = true;
        const TIMER = 5000;
        // We display an information only, the app is updated automatically after 5 seconds
        this.timer = TIMER;
        let timerInterval = setInterval(() => {
          this.timer = this.timer - 1000;
        }, 1000);

        setTimeout(() => {
          this.$nextTick(() => {
            clearInterval(timerInterval);
            this.refreshApp();
          });
        }, TIMER);
      }
    },

    refreshApp() {
      console.log('send skipWaiting and reload app');

      this.appUpdateExists = false;
      this.updateExists = false;
      this.isRefreshingOnRealAppUpdate = true;
      if (!this.registration || !this.registration.waiting) {
        return;
      }
      this.registration.waiting.postMessage('skipWaiting');
    },
  },

  watch: {
    isReadyAndDataLoaded() {
      console.log('entered ready state');
      this.showInstallPrompt();
    },
    isAuthenticated(newValue) {
      if (this.isPwaMode && newValue) {
        console.log('showInstallPrompt after the user has been authenticated');
        this.showInstallPrompt();
      }
    },
  },
};
</script>
